import Case from 'case';
import {
	Chart as ChartJS,
	CategoryScale,
	LinearScale,
	BarElement,
	ArcElement,
	Title,
	Tooltip,
	Legend,
} from 'chart.js';
import React, { useState, useMemo, useCallback } from 'react';
import { Bar, Doughnut } from 'react-chartjs-2';
import { View, ScrollView, StyleSheet, Pressable } from 'react-native';

import { Text, useTheme, Chip, ActivityIndicator, Searchbar } from '@/components/ui';
import { dataService } from '@/services/data-service';
import { getUserColor, GRAYED_USER_COLOR } from '@/utils/user-colors';

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Title, Tooltip, Legend);

const CHANGE_TYPE_COLORS: Record<string, string> = {
	create: '#4CAF50',
	update: '#2196F3',
	delete: '#F44336',
};

type HistoryRecord = {
	id: string;
	objectType: string;
	objectId: string;
	changeType: string;
	property?: string;
	ts: number;
	userId?: string;
	comment?: string;
	transactionId?: string;
	oldValue?: unknown;
	newValue?: unknown;
};

const formatValue = (v: unknown): string => {
	if (v === null || v === undefined) return '(empty)';
	if (typeof v === 'string') return v;
	return JSON.stringify(v);
};

export const HistoryDashboard: React.FC = () => {
	const theme = useTheme();
	const [selectedUser, setSelectedUser] = useState<string | null>(null);
	const [searchQuery, setSearchQuery] = useState('');
	const [historyPage, setHistoryPage] = useState(1);
	const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

	const [stats, setStats] = useState<{ byUser: any[]; byObjectType: any[]; byDay: any[]; byUserDay: any[] } | null>(null);
	const [history, setHistory] = useState<{ data: HistoryRecord[]; pagination: any } | null>(null);
	const [users, setUsers] = useState<Record<string, string>>({});
	const [userColors, setUserColors] = useState<Record<string, string>>({});
	const [loading, setLoading] = useState(true);
	const [historyLoading, setHistoryLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const sinceMs = useMemo(() => Date.now() - 30 * 86400000, []);

	const fetchStats = useCallback(async (signal?: AbortSignal) => {
		setLoading(true);
		setError(null);
		try {
			const [statsResult, usersResult] = await Promise.all([
				dataService.getHistoryStats({ since: sinceMs }),
				dataService.getUsers({ limit: 500, includeArchived: true }),
			]);
			if (signal?.aborted) return;
			setStats(statsResult);
			const userMap: Record<string, string> = {};
			const colorMap: Record<string, string> = {};
			for (const u of usersResult.data) {
				userMap[u.id] = u.name;
				if (u.color) colorMap[u.id] = u.color;
			}
			setUsers(userMap);
			setUserColors(colorMap);
		} catch (err) {
			if (signal?.aborted) return;
			console.error('Failed to load history stats:', err);
			setError('Failed to load history stats. Please try again.');
		}
		setLoading(false);
	}, [sinceMs]);

	const fetchHistory = useCallback(async (signal?: AbortSignal) => {
		setHistoryLoading(true);
		try {
			const result = await dataService.getHistory({
				since: sinceMs,
				userId: selectedUser || undefined,
				page: historyPage,
				limit: 50,
			});
			if (signal?.aborted) return;
			setHistory(result);
		} catch (err) {
			if (signal?.aborted) return;
			console.error('Failed to load history:', err);
			setError('Failed to load history. Please try again.');
		}
		setHistoryLoading(false);
	}, [sinceMs, selectedUser, historyPage]);

	React.useEffect(() => {
		const controller = new AbortController();
		fetchStats(controller.signal);
		return () => controller.abort();
	}, [fetchStats]);

	React.useEffect(() => {
		const controller = new AbortController();
		fetchHistory(controller.signal);
		return () => controller.abort();
	}, [fetchHistory]);

	// Both charts color by user id off the persisted per-user color, so each
	// person is the same color everywhere. Falls back to a deterministic hash
	// for users with no color yet (or the synthetic `unknown` bucket).
	const colorFor = useCallback(
		(uid: string) => userColors[uid] || getUserColor(uid),
		[userColors],
	);

	const userTimelineData = useMemo(() => {
		if (!stats?.byUserDay) return null;

		const rows = selectedUser
			? stats.byUserDay.filter((r: any) => r.user_id === selectedUser)
			: stats.byUserDay;

		const allDates = new Set<string>();
		const userDayMap: Record<string, Record<string, number>> = {};
		const userNames: Record<string, string> = {};

		for (const row of rows) {
			allDates.add(row.date);
			const uid = row.user_id || 'unknown';
			if (!userDayMap[uid]) userDayMap[uid] = {};
			userDayMap[uid][row.date] = (userDayMap[uid][row.date] || 0) + row.count;
			if (row.user_name) userNames[uid] = row.user_name;
		}

		const sortedDates = Array.from(allDates).sort();
		const userIds = Object.keys(userDayMap)
			.sort((a, b) => {
				const totalA = Object.values(userDayMap[a]).reduce((s, n) => s + n, 0);
				const totalB = Object.values(userDayMap[b]).reduce((s, n) => s + n, 0);
				return totalB - totalA;
			});

		return {
			labels: sortedDates.map(d => d.slice(5)),
			userIds,
			datasets: userIds.map((uid) => ({
				label: userNames[uid] || users[uid] || uid.slice(0, 8),
				data: sortedDates.map(d => userDayMap[uid][d] || 0),
				backgroundColor: colorFor(uid),
			})),
		};
	}, [stats, users, selectedUser, colorFor]);

	const userPieData = useMemo(() => {
		if (!stats?.byUser) return null;
		const totals: Record<string, { name: string; count: number }> = {};
		for (const row of stats.byUser) {
			const uid = row.user_id || 'unknown';
			if (!totals[uid]) totals[uid] = { name: row.user_name || users[uid] || uid.slice(0, 8), count: 0 };
			totals[uid].count += row.count;
		}
		const sorted = Object.entries(totals).sort(([, a], [, b]) => b.count - a.count);
		return {
			labels: sorted.map(([, t]) => t.name),
			userIds: sorted.map(([uid]) => uid),
			datasets: [{
				data: sorted.map(([, t]) => t.count),
				backgroundColor: sorted.map(([uid]) =>
					selectedUser && uid !== selectedUser ? GRAYED_USER_COLOR : colorFor(uid),
				),
			}],
		};
	}, [stats, users, selectedUser, colorFor]);

	const filteredHistory = useMemo(() => {
		if (!history?.data) return [];
		if (!searchQuery.trim()) return history.data;
		const q = searchQuery.trim().toLowerCase();
		return history.data.filter(h =>
			h.objectType.toLowerCase().includes(q)
			|| h.objectId.toLowerCase().includes(q)
			|| (h.changeType || '').toLowerCase().includes(q)
			|| (h.property || '').toLowerCase().includes(q)
			|| (h.comment || '').toLowerCase().includes(q)
			|| (users[h.userId || ''] || '').toLowerCase().includes(q)
			|| formatValue(h.oldValue).toLowerCase().includes(q)
			|| formatValue(h.newValue).toLowerCase().includes(q),
		);
	}, [history, searchQuery, users]);

	const toggleUser = useCallback((userId: string | null) => {
		if (!userId) return;
		setSelectedUser(prev => prev === userId ? null : userId);
		setHistoryPage(1);
	}, []);

	const clearFilter = useCallback(() => {
		setSelectedUser(null);
		setHistoryPage(1);
	}, []);

	const handleTimelineClick = useCallback((_event: any, elements: any[]) => {
		if (!elements.length || !userTimelineData) return;
		const uid = userTimelineData.userIds[elements[0].datasetIndex];
		toggleUser(uid);
	}, [userTimelineData, toggleUser]);

	const handlePieClick = useCallback((_event: any, elements: any[]) => {
		if (!elements.length || !userPieData) return;
		const uid = userPieData.userIds[elements[0].index];
		toggleUser(uid);
	}, [userPieData, toggleUser]);

	const handleTimelineLegendClick = useCallback((_event: any, legendItem: any) => {
		if (!userTimelineData) return;
		const uid = userTimelineData.userIds[legendItem.datasetIndex];
		toggleUser(uid);
	}, [userTimelineData, toggleUser]);

	const barChartOptions = useMemo(() => ({
		responsive: true,
		maintainAspectRatio: false,
		onClick: handleTimelineClick,
		plugins: {
			legend: {
				position: 'top' as const,
				onClick: handleTimelineLegendClick,
			},
		},
		scales: {
			x: { stacked: true },
			y: { stacked: true, beginAtZero: true },
		},
	}), [handleTimelineClick, handleTimelineLegendClick]);

	const pieChartOptions = useMemo(() => ({
		responsive: true,
		maintainAspectRatio: false,
		onClick: handlePieClick,
		plugins: {
			// Colors are shared with the bar chart, which carries the single legend,
			// so hide this one and let the doughnut use the full width.
			legend: { display: false },
		},
	}), [handlePieClick]);

	return (
		<ScrollView style={[styles.container, { backgroundColor: theme.colors.background }]}>
			{error && (
				<View style={{ padding: 16, marginBottom: 12, backgroundColor: theme.colors.errorContainer, borderRadius: 8 }}>
					<Text style={{ color: theme.colors.onErrorContainer }}>{error}</Text>
				</View>
			)}
			{loading ? (
				<ActivityIndicator size="large" style={{ marginTop: 40 }} />
			) : (
				<>
					{/* Top Row: Bar Chart (2/3) + Pie Chart (1/3) */}
					<View style={styles.topRow}>
						<View style={[styles.topLeft, { backgroundColor: theme.colors.surface, borderColor: theme.colors.surfaceVariant }]}>
							<View style={styles.chartHeader}>
								<Text style={styles.chartTitle}>Work orders created by user by day</Text>
							</View>
							<View style={{ height: 220 }}>
								{userTimelineData && <Bar data={userTimelineData} options={barChartOptions} />}
							</View>
						</View>
						<View style={[styles.topRight, { backgroundColor: theme.colors.surface, borderColor: theme.colors.surfaceVariant }]}>
							<Text style={styles.chartTitle}>Activity by User</Text>
							<View style={{ height: 260 }}>
								{userPieData && <Doughnut data={userPieData} options={pieChartOptions} />}
							</View>
						</View>
					</View>

					{/* Active Filter Indicator */}
					{selectedUser && (
						<View style={styles.activeFilterRow}>
							<Chip
								mode="flat"
								style={{ backgroundColor: theme.colors.primaryContainer }}
							>
								<Text>Filtered: {users[selectedUser] || selectedUser.slice(0, 8)}</Text>
							</Chip>
							<Pressable onPress={clearFilter}>
								<Text style={{ color: theme.colors.error, fontSize: 13 }}>✕ Clear filter</Text>
							</Pressable>
						</View>
					)}

					{/* Activity Log */}
					<View style={[styles.tableCard, { backgroundColor: theme.colors.surface, borderColor: theme.colors.surfaceVariant }]}>
						<Text style={styles.chartTitle}>Activity Log</Text>

						<Searchbar
							placeholder="Search everything..."
							value={searchQuery}
							onChangeText={setSearchQuery}
							style={styles.searchBar}
						/>

						{historyLoading ? (
							<ActivityIndicator size="small" style={{ marginVertical: 20 }} />
						) : (
							<>
								{/* Table Header */}
								<View style={[styles.tableRow, styles.tableHeader, { borderColor: theme.colors.surfaceVariant }]}>
									<Text style={[styles.tableCell, styles.cellTime, styles.headerText]}>Time</Text>
									<Text style={[styles.tableCell, styles.cellUser, styles.headerText]}>User</Text>
									<Text style={[styles.tableCell, styles.cellSummary, styles.headerText]}>Summary</Text>
								</View>

								{/* Table Rows */}
								{filteredHistory.map((record) => {
									const isExpanded = expandedRows.has(record.id);
									const hasValues = record.newValue !== undefined || record.oldValue !== undefined;
									const summary = record.changeType === 'update' && record.property
										? `${record.property}: ${formatValue(record.newValue).slice(0, 100)}${formatValue(record.newValue).length > 100 ? '...' : ''}`
										: record.changeType === 'create'
											? `created ${Case.title(record.objectType.replace(/_/g, ' '))}`
											: record.changeType === 'delete'
												? `deleted ${Case.title(record.objectType.replace(/_/g, ' '))}`
												: record.property || '—';
									return (
										<React.Fragment key={record.id}>
											<Pressable
												onPress={() => {
													if (!hasValues) return;
													setExpandedRows(prev => {
														const next = new Set(prev);
														if (next.has(record.id)) next.delete(record.id);
														else next.add(record.id);
														return next;
													});
												}}
												style={[styles.tableRow, { borderColor: theme.colors.surfaceVariant, cursor: hasValues ? 'pointer' : 'default' } as any]}
											>
												<Text style={[styles.tableCell, styles.cellTime, { color: theme.colors.onSurfaceVariant }]}>
													{new Date(record.ts).toLocaleString()}
												</Text>
												<Text style={[styles.tableCell, styles.cellUser]} numberOfLines={1}>
													{users[record.userId || ''] || record.userId?.slice(0, 8) || '—'}
												</Text>
												<View style={[styles.tableCell, styles.cellSummary, { flexDirection: 'row', alignItems: 'center', gap: 8 }] as any}>
													<Chip
														mode="flat"
														compact
														textStyle={{ color: '#fff', fontSize: 11 }}
														style={{ backgroundColor: CHANGE_TYPE_COLORS[record.changeType] || '#999' }}
													>
														<Text style={{ color: '#fff', fontSize: 11 }}>{record.changeType}</Text>
													</Chip>
													<Text style={{ color: theme.colors.onSurfaceVariant, flex: 1, fontSize: 13 }} numberOfLines={1}>
														{hasValues ? (isExpanded ? '▼ ' : '▶ ') : ''}{summary}
													</Text>
												</View>
											</Pressable>
											{isExpanded && (
												<View style={[styles.expandedRow, { backgroundColor: theme.colors.surfaceVariant + '44', borderColor: theme.colors.surfaceVariant }]}>
													{record.property && (
														<Text style={[styles.expandedLabel, { color: theme.colors.onSurfaceVariant }]}>
															Property: <Text style={{ fontWeight: '700' }}>{record.property}</Text>
														</Text>
													)}
													{record.oldValue !== undefined && (
														<View style={styles.expandedValueBlock}>
															<Text style={[styles.expandedValueLabel, { color: theme.colors.error }]}>Old value:</Text>
															<Text style={[styles.expandedValue, { color: theme.colors.onSurface }]} selectable>
																{formatValue(record.oldValue)}
															</Text>
														</View>
													)}
													{record.newValue !== undefined && (
														<View style={styles.expandedValueBlock}>
															<Text style={[styles.expandedValueLabel, { color: theme.colors.primary }]}>New value:</Text>
															<Text style={[styles.expandedValue, { color: theme.colors.onSurface }]} selectable>
																{formatValue(record.newValue)}
															</Text>
														</View>
													)}
												</View>
											)}
										</React.Fragment>
									);
								})}

								{filteredHistory.length === 0 && (
									<View style={styles.emptyRow}>
										<Text style={{ color: theme.colors.onSurfaceVariant }}>No history records found.</Text>
									</View>
								)}

								{/* Pagination */}
								{history?.pagination && history.pagination.pages > 1 && (
									<View style={styles.paginationRow}>
										<Chip
											mode="outlined"
											onPress={() => setHistoryPage(p => Math.max(1, p - 1))}
											disabled={historyPage <= 1}
										>
											<Text>Previous</Text>
										</Chip>
										<Text style={{ color: theme.colors.onSurfaceVariant }}>
											Page {history.pagination.page} of {history.pagination.pages} ({history.pagination.total} records)
										</Text>
										<Chip
											mode="outlined"
											onPress={() => setHistoryPage(p => Math.min(history!.pagination.pages, p + 1))}
											disabled={historyPage >= history.pagination.pages}
										>
											<Text>Next</Text>
										</Chip>
									</View>
								)}
							</>
						)}
					</View>
				</>
			)}
		</ScrollView>
	);
};

const styles = StyleSheet.create({
	container: {
		flex: 1,
		padding: 16,
	},
	topRow: {
		flexDirection: 'row',
		gap: 16,
		marginBottom: 16,
	} as any,
	topLeft: {
		flex: 2,
		borderRadius: 12,
		borderWidth: 1,
		padding: 16,
	},
	topRight: {
		flex: 1,
		borderRadius: 12,
		borderWidth: 1,
		padding: 16,
	},
	chartHeader: {
		flexDirection: 'row',
		justifyContent: 'space-between',
		alignItems: 'center',
		marginBottom: 12,
	} as any,
	chartTitle: {
		fontSize: 16,
		fontWeight: '700',
		marginBottom: 12,
	},
	activeFilterRow: {
		flexDirection: 'row',
		alignItems: 'center',
		gap: 8,
		marginBottom: 12,
	} as any,
	tableCard: {
		borderRadius: 12,
		borderWidth: 1,
		padding: 16,
		marginBottom: 32,
	},
	searchBar: {
		marginBottom: 12,
	},
	tableRow: {
		flexDirection: 'row',
		borderBottomWidth: 1,
		paddingVertical: 8,
		alignItems: 'center',
	} as any,
	tableHeader: {
		borderBottomWidth: 2,
	},
	headerText: {
		fontWeight: '700',
		fontSize: 12,
		textTransform: 'uppercase',
		letterSpacing: 0.5,
	} as any,
	tableCell: {
		paddingHorizontal: 4,
		fontSize: 13,
	},
	cellTime: { width: 170 },
	cellUser: { width: 130 },
	cellSummary: { flex: 1, minWidth: 300 },
	expandedRow: {
		paddingHorizontal: 16,
		paddingVertical: 12,
		borderBottomWidth: 1,
		marginLeft: 170,
	},
	expandedLabel: {
		fontSize: 13,
		marginBottom: 8,
	},
	expandedValueBlock: {
		marginBottom: 8,
	},
	expandedValueLabel: {
		fontSize: 11,
		fontWeight: '600' as const,
		marginBottom: 2,
	},
	expandedValue: {
		fontSize: 13,
		fontFamily: 'monospace',
		lineHeight: 18,
	} as any,
	emptyRow: {
		paddingVertical: 24,
		alignItems: 'center',
	},
	paginationRow: {
		flexDirection: 'row',
		justifyContent: 'center',
		alignItems: 'center',
		gap: 16,
		paddingTop: 12,
	} as any,
});
